import { PinInput } from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, InputField } from '../../../../../components/Atoms';
import { useAppSelector } from '../../../../../hooks/useAppSelector';
import {
    PAGE_TYPE,
    PageType,
    VERIFICATION_STATE_TYPE,
    VerificationStateType,
} from '../../constants';
import { useRequestResetPassword } from './hooks/useRequestResetPassword';
import { useResetPassword } from './hooks/useResetPassword';

interface ResetPasswordFormProps {
    setPageType: Dispatch<SetStateAction<PageType>>;
}

export default function ResetPasswordForm({ setPageType }: ResetPasswordFormProps) {
    const { t } = useTranslation(['auth', 'validation']);
    const errorMessage = useAppSelector((state) => state.error.message);

    const [verificationState, setVerificationState] = useState<VerificationStateType>(
        VERIFICATION_STATE_TYPE.REQUEST
    );

    const [requestUserId, setRequestUserId] = useState<number | undefined>();

    const {
        isSubmitting: isRequestingSubmitting,
        errors: requestErrors,
        onSubmit: onRequestSubmit,
        register: requestRegister,
    } = useRequestResetPassword({
        setVerificationState,
        setRequestUserId,
    });
    const {
        isSubmitting: isResettingSubmitting,
        onSubmit: handleResetSubmit,
        setValue: setResetValue,
        register: resetRegister,
        watch: resetWatch,
        onSubmit: onResetSubmit,
    } = useResetPassword({ setPageType, requestUserId });

    if (verificationState === VERIFICATION_STATE_TYPE.VERIFY) {
        return (
            <form
                autoComplete="off"
                onSubmit={onResetSubmit}
                className="w-full flex flex-col justify-center items-start"
            >
                <h1 className="text-base font-bold text-fg">{t('resetPassword.otpTitle')}</h1>
                <span className="text-sm text-fg/78">{t('resetPassword.otpSentToPhone')}</span>

                <div className="w-full flex flex-col justify-center items-center gap-6 my-4">
                    <PinInput
                        size={'sm'}
                        length={6}
                        value={resetWatch('otpCode')}
                        onChange={(value) => setResetValue('otpCode', value)}
                        styles={{
                            input: {
                                textAlign: 'center',
                            },
                        }}
                    />
                </div>

                <div className="w-full flex flex-col justify-center items-center gap-3 my-3">
                    <InputField
                        label={t('resetPassword.newPasswordLabel')}
                        size="sm"
                        type="password"
                        placeholder={t('resetPassword.newPasswordLabel')}
                        passwordReveal
                        {...resetRegister('password', {
                            required: t('validation:password.required'),
                            minLength: {
                                value: 8,
                                message: t('validation:password.tooShort'),
                            },
                        })}
                        required
                    />
                    <InputField
                        label={t('resetPassword.confirmNewPasswordLabel')}
                        size="sm"
                        type="password"
                        placeholder={t('resetPassword.confirmNewPasswordLabel')}
                        passwordReveal
                        {...resetRegister('confirmPassword', {
                            required: t('validation:confirmPassword.required'),
                            validate: (value) =>
                                value === resetWatch('password') ||
                                (t('validation:password.mismatch') as string),
                        })}
                        required
                    />
                </div>

                <div className="mt-1 w-full flex flex-row justify-center items-center gap-2 flex-nowrap">
                    <Button
                        type="button"
                        variant={'secondary'}
                        size="sm"
                        width={'100%'}
                        onClick={() => setPageType(PAGE_TYPE.SIGN_IN)}
                    >
                        {t('resetPassword.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        isLoading={isResettingSubmitting}
                        size="sm"
                        width={'100%'}
                    >
                        {t('resetPassword.reset')}
                    </Button>
                </div>
            </form>
        );
    }

    return (
        <form
            autoComplete="off"
            onSubmit={onRequestSubmit}
            className="w-full flex flex-col justify-center items-start"
        >
            <h1 className="text-base font-bold text-fg">{t('resetPassword.title')}</h1>
            <p className="text-sm text-fg/78 mb-4">{t('resetPassword.requestSubtitle')}</p>

            <InputField
                label={t('resetPassword.emailLabel')}
                size="md"
                type="email"
                placeholder={t('resetPassword.emailLabel')}
                className="mb-2"
                required
                {...requestRegister('email', {
                    required: t('validation:email.required'),
                    pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('validation:email.invalid'),
                    },
                })}
                error={requestErrors.email?.message}
            />

            {errorMessage && (
                <p className="text-red-400 light:text-red-700 text-sm">{errorMessage}</p>
            )}

            <div className="mt-4 w-full flex flex-row justify-center items-center gap-2">
                <Button
                    type="button"
                    variant={'secondary'}
                    size="sm"
                    width={'100%'}
                    onClick={() => setPageType(PAGE_TYPE.SIGN_IN)}
                >
                    {t('resetPassword.cancel')}
                </Button>
                <Button
                    type="submit"
                    size="sm"
                    width={'100%'}
                    isLoading={isRequestingSubmitting}
                    leftIcon={<IconSend size={16} stroke={1.5} />}
                >
                    {t('resetPassword.sendOtp')}
                </Button>
            </div>
        </form>
    );
}
